import { CiBar } from "../shared/CiBar";
import { Badge } from "../shared/Badge";
import { SVI_COLORS } from "../../lib/colormap";
import { THEME_LABELS } from "../../lib/constants";
import { fmtPct } from "../../lib/formatters";
import type { EquityFinding } from "../../types/equity";
import { useUIStore } from "../../store/uiStore";
import { drawerPalette, type DrawerPalette } from "../../lib/drawerTheme";

interface Props {
  equity: EquityFinding;
}

export function EquitySection({ equity }: Props) {
  const theme = useUIStore((s) => s.theme);
  const palette = drawerPalette(theme);
  return (
    <section>
      <SectionTitle palette={palette}>Equity Audit</SectionTitle>

      {equity.insufficient_data && (
        <Notice palette={palette}>SVI data unavailable — using paper-headline fallback values.</Notice>
      )}

      <div style={{ display: "flex", gap: 6, marginBottom: 10, flexWrap: "wrap" }}>
        <Badge
          label={`SVI ${equity.svi_quintile}`}
          color={SVI_COLORS[equity.svi_quintile]}
          textColor={equity.svi_quintile === "Q3" ? "#374151" : "#fff"}
        />
        <Badge
          label={THEME_LABELS[equity.dominant_theme]}
          color="#6366f1"
        />
      </div>

      <SubTitle palette={palette}>ΔEO (Equal Opportunity Gap)</SubTitle>
      <CiBar
        estimate={equity.delta_eo_global}
        lower={equity.delta_eo_global_ci.lower}
        upper={equity.delta_eo_global_ci.upper}
        min={-0.5}
        max={0.5}
      />

      <div style={{ height: 10 }} />

      <SubTitle palette={palette}>ρ (Risk-SVI Correlation)</SubTitle>
      <CiBar
        estimate={equity.rho_global}
        lower={equity.rho_global_ci.lower}
        upper={equity.rho_global_ci.upper}
        min={-3}
        max={3}
      />

      <div style={{ height: 10 }} />

      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "6px 12px" }}>
        <KV palette={palette} label="Historical Recall" value={fmtPct(equity.historical_recall)} />
        <KV palette={palette} label="Historical FNR" value={fmtPct(equity.historical_fnr)} />
      </div>

      {equity.model_family_disagreement && (
        <div style={{ fontSize: 12, color: palette.noticeWarnText, marginTop: 8 }}>
          ⚠ Model disagreement: {equity.model_family_disagreement}
        </div>
      )}

      {equity.one_sentence_caveat && (
        <div style={{ fontSize: 12, color: palette.italicCaveat, fontStyle: "italic", marginTop: 6 }}>
          {equity.one_sentence_caveat}
        </div>
      )}
    </section>
  );
}

function SectionTitle({ children, palette }: { children: React.ReactNode; palette: DrawerPalette }) {
  return (
    <div style={{ fontWeight: 700, fontSize: 13, color: palette.sectionTitle, marginBottom: 8, textTransform: "uppercase", letterSpacing: "0.06em" }}>
      {children}
    </div>
  );
}

function SubTitle({ children, palette }: { children: React.ReactNode; palette: DrawerPalette }) {
  return (
    <div style={{ fontSize: 11, fontWeight: 600, color: palette.subtitle, marginBottom: 4, textTransform: "uppercase", letterSpacing: "0.05em" }}>
      {children}
    </div>
  );
}

function KV({ label, value, palette }: { label: string; value: string; palette: DrawerPalette }) {
  return (
    <div>
      <div style={{ fontSize: 10, color: palette.kvLabel, textTransform: "uppercase", letterSpacing: "0.05em" }}>{label}</div>
      <div style={{ fontSize: 13, color: palette.kvValue, fontWeight: 500 }}>{value}</div>
    </div>
  );
}

function Notice({ children, palette }: { children: React.ReactNode; palette: DrawerPalette }) {
  return (
    <div style={{ background: palette.noticeWarnBg, color: palette.noticeWarnText, border: `1px solid ${palette.noticeWarnBorder}`, fontSize: 12, padding: "6px 10px", borderRadius: 4, marginBottom: 8 }}>
      {children}
    </div>
  );
}
